import React, { useImperativeHandle, useRef, useEffect } from 'react';
import { useReactive } from 'ahooks';
import { Button, Dropdown, message, Progress } from 'kenshin';
import {
  SearchOutlined,
  AppstoreOutlined,
  DownloadOutlined,
  CopyOutlined,
  StarOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  EnterOutlined,
} from '@ant-design/icons';
import {
  buildImagePromptSetting,
  buildVideoPromptSetting,
  buildChatPromptSetting,
  findImageModelByCode,
  findVideoModelByCode,
} from '../../config/models';
import {
  addAiSessionItem,
  refreshAiSessionItem,
  getCurrentQuota,
} from '@/_serveice/aiCreation';
import InputArea from '../InputArea';
import ImagePreview from '../ImagePreview';
import VideoPreview from '../VideoPreview';
import GeneratingCard from '../GeneratingCard';
import FailedCard from '../FailedCard';
import MessageSkeleton from '../MessageSkeleton';
import MarkdownRender from '../MarkdownRender';
import {
  isVideoUrl,
  getVideoCoverUrl,
  downloadFileUrl,
  OSSImgUrl,
  getFileNameFromUrl,
} from '@/_util/util';
import './index.less';

const MODES = {
  CHAT: 'chat',
  IMAGE: 'image',
  VIDEO: 'video',
};

// 格式化数字为万单位
function formatQuota(num) {
  if (!num && num !== 0) return '-';
  if (num >= 10000) {
    return `${(num / 10000).toFixed(2)}万`;
  }
  return num.toLocaleString();
}

// 获取当前员工ID
function getEmpId() {
  try {
    const emp = JSON.parse(localStorage.getItem('employeeDTO'));
    return emp?.empId;
  } catch {
    return null;
  }
}

const ChatArea = React.forwardRef((props, ref) => {
  const {
    mode,
    conversationId,
    conversationLoading,
    onModeChange,
    onToggleAssetLibrary,
    onSessionIdChange,
    onRefreshList,
  } = props;
  const messagesEndRef = useRef();
  const pollingTimerRef = useRef(null);
  const inputAreaRef = useRef(null); // InputArea 的 ref

  const state = useReactive({
    messages: [],
    loading: false,
    previewImage: null, // { images: [], index: 0 }
    previewVideo: null,
    quota: null, // 配额信息 { totalQuota, usedQuota }
  });

  // 清理轮询定时器
  const clearPolling = () => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  };

  // 滚动到底部
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useImperativeHandle(ref, () => ({
    clearMessages: () => {
      state.messages = [];
    },
    addMessage: (msg) => {
      state.messages = [...state.messages, msg];
    },
    // 拖拽文件上传 - 透传给 InputArea
    addDroppedFiles: (files) => {
      inputAreaRef.current?.addDroppedFiles(files);
    },
    // 加载历史消息（对接会话详情接口后调用）
    loadHistory: (items) => {
      const msgs = items
        .map((item, idx) => {
          const promptData = JSON.parse(item.promptSetting || '{}');
          const userMsg = {
            id: `user-${item.itemId || idx}`,
            role: 'user',
            content: promptData.prompt || '',
            images: promptData.images || promptData.referenceImages || [],
            time: item.submitTime || '',
          };
          const quantity = promptData.number || 1;
          const modelCode = promptData.modelCode || '';
          const genType = item.genType || 1;

          // 根据 genType 确定模式
          let mode;
          if (genType === 1) {
            mode = MODES.IMAGE;
          } else if (genType === 2) {
            mode = MODES.VIDEO;
          } else if (genType === 3) {
            mode = MODES.CHAT;
          } else {
            mode = MODES.IMAGE; // 默认图片
          }

          const assistantMsg = {
            id: `assistant-${item.itemId || idx}`,
            role: 'assistant',
            content:
              item.state === 2
                ? `生成失败: ${item.errorMsg || '未知错误'}`
                : item.state === 0
                ? genType === 3
                  ? '正在思考中...'
                  : '正在生成中，请稍候...'
                : genType === 3
                ? item.outputList?.[0]?.textContent ||
                  item.outputList?.[0]?.content ||
                  ''
                : '生成完成',
            time: item.finishTime || '',
            images: (
              item.outputList
                ?.filter((o) => o.resourceUrl)
                .map((o) => o.resourceUrl) || []
            ).filter((u) => !isVideoUrl(u)),
            videos: (
              item.outputList
                ?.filter((o) => o.resourceUrl)
                .map((o) => o.resourceUrl) || []
            ).filter((u) => isVideoUrl(u)),
            state: item.state, // 0-进行中 1-已完成 2-失败
            itemId: item.itemId,
            genType,
            remoteTaskId: item.remoteTaskId,
            quantity, // 从 promptSetting 解析的生成数量
            modelCode, // 从 promptSetting 解析的模型代码
            mode, // 添加模式字段
          };
          return [userMsg, assistantMsg];
        })
        .flat();
      state.messages = msgs;

      // 检查是否有进行中的消息
      const hasInProgress = items.some((item) => item.state === 0);
      if (hasInProgress) {
        state.loading = true;
      }

      // 对进行中的消息重新启动轮询
      items.forEach((item, idx) => {
        if (item.state === 0 && item.itemId) {
          const assistantMsgId = `assistant-${item.itemId || idx}`;
          startPolling(item.itemId, assistantMsgId, item.genType || 1);
        }
      });

      // 滚动到底部
      scrollToBottom();
    },
  }));

  // 轮询刷新生成项
  const startPolling = (itemId, assistantMsgId, genType) => {
    const poll = async () => {
      try {
        const res = await refreshAiSessionItem({ itemId });
        if (!res?.success || !res?.data) {
          // 刷新失败，停止轮询
          const msgIndex = state.messages.findIndex(
            (m) => m.id === assistantMsgId,
          );
          if (msgIndex !== -1) {
            state.messages[msgIndex].content = '生成失败，请重试';
            state.messages[msgIndex].state = 2;
          }
          state.loading = false;
          return;
        }

        const itemData = res.data;
        const msgIndex = state.messages.findIndex(
          (m) => m.id === assistantMsgId,
        );
        if (msgIndex === -1) {
          state.loading = false;
          return;
        }

        if (itemData.state === 0) {
          // 进行中，继续轮询
          pollingTimerRef.current = setTimeout(poll, 3000);
        } else if (itemData.state === 1) {
          // 已完成
          state.messages[msgIndex].state = 1;
          const urls =
            itemData.outputList
              ?.filter((o) => o.resourceUrl)
              .map((o) => o.resourceUrl) || [];

          // 聊天模式：只返回文本，需要从 outputList 中提取文本内容
          if (state.messages[msgIndex].mode === MODES.CHAT) {
            state.messages[msgIndex].content =
              itemData.outputList?.[0]?.textContent ||
              itemData.outputList?.[0]?.content ||
              '已完成';
          } else {
            // 图片/视频模式
            state.messages[msgIndex].content = '生成完成';
            state.messages[msgIndex].images = urls.filter(
              (u) => !isVideoUrl(u),
            );
            state.messages[msgIndex].videos = urls.filter((u) => isVideoUrl(u));
          }
          state.loading = false;
          // 更新配额信息
          fetchQuota();
        } else if (itemData.state === 2) {
          // 失败
          state.messages[msgIndex].content = `生成失败: ${
            itemData.errorMsg || '未知错误'
          }`;
          state.messages[msgIndex].state = 2;
          state.loading = false;
          // 更新配额信息
          fetchQuota();
        }
      } catch (err) {
        console.error('轮询失败:', err);
        const msgIndex = state.messages.findIndex(
          (m) => m.id === assistantMsgId,
        );
        if (msgIndex !== -1) {
          state.messages[msgIndex].content = '生成失败，请重试';
          state.messages[msgIndex].state = 2;
        }
        state.loading = false;
      }
    };

    // 3秒后开始第一次轮询
    pollingTimerRef.current = setTimeout(poll, 3000);
  };

  // 发送消息
  const handleSend = async (data) => {
    const empId = getEmpId();
    if (!empId) {
      message.error('未获取到员工信息，请重新登录');
      return;
    }

    // 添加用户消息
    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: data.text,
      time: new Date().toLocaleString(),
      images: data.images || [],
    };
    state.messages = [...state.messages, userMsg];
    state.loading = true;

    // 滚动到底部
    scrollToBottom();

    // 构建 promptSetting (JSON字符串)
    let promptSetting;
    let genType;
    let modelCode;

    if (data.mode === MODES.IMAGE) {
      genType = 1; // 1-图片
      modelCode = data.params.modelCode;
      const model = findImageModelByCode(modelCode);
      let prompt = data.text;

      if (modelCode === 'M-0003-0001' && !data.setting.lastTaskId) {
        prompt = '帮我生成图片：' + prompt;
      }
      promptSetting = buildImagePromptSetting({
        modelCode,
        prompt,
        number: data.params.quantity,
        size: data.params.size,
        width: data.params.width,
        height: data.params.height,
        images: data.images || [],
        setting: data.setting || (model?.hasSetting ? {} : undefined),
      });
      debugger;
    } else if (data.mode === MODES.VIDEO) {
      genType = 2; // 2-视频
      modelCode = data.params.modelCode;
      const model = findVideoModelByCode(modelCode);
      promptSetting = buildVideoPromptSetting({
        modelCode,
        prompt: data.text,
        ratio: data.params.ratio,
        duration: data.params.duration,
        resolution: data.params.resolution,
        referenceImages: data.images || [],
      });
    } else if (data.mode === MODES.CHAT) {
      genType = 3; // 3-聊天
      modelCode = data.params.modelCode;
      promptSetting = buildChatPromptSetting({
        modelCode,
        prompt: data.text,
      });
    }

    // 先添加"正在生成中"消息
    const assistantMsgId = `assistant-${Date.now()}`;
    const quantity = data.params.quantity || 1;
    const assistantMsg = {
      id: assistantMsgId,
      role: 'assistant',
      content:
        data.mode === MODES.CHAT ? '正在思考中...' : '正在生成中，请稍候...',
      time: new Date().toLocaleString(),
      images: [],
      videos: [],
      state: 0, // 0-进行中
      quantity, // 生成数量
      genType,
      modelCode, // 存储模型代码，用于判断是否显示"引用"按钮
      mode: data.mode, // 存储模式，用于判断渲染逻辑
    };
    state.messages = [...state.messages, assistantMsg];

    // 滚动到底部
    scrollToBottom();

    // 调用新增生成接口
    try {
      const res = await addAiSessionItem({
        empId,
        genType,
        promptSetting,
        sessionId: conversationId || null,
      });

      if (res?.success && res?.data) {
        let i = state.messages.findIndex((m) => m.id === assistantMsgId);
        if (i !== -1) {
          state.messages[i] = {
            ...res.data,
            ...state.messages[i],
          };
        }
        const { itemId, sessionId } = res.data;

        // 通知父组件更新 sessionId（用于下次会话）
        if (sessionId) {
          onSessionIdChange?.(sessionId);
        }

        // 通知父组件：刷新左侧列表
        onRefreshList?.();

        // 启动轮询
        if (itemId) {
          startPolling(itemId, assistantMsgId, genType);
        }
      } else {
        // 更新消息为失败状态
        const msg = state.messages.find((m) => m.id === assistantMsgId);
        if (msg) {
          msg.content = res?.errorMessage || '生成失败';
          msg.state = 2;
        }
        state.loading = false;
      }
    } catch (err) {
      console.error('调用新增生成接口失败:', err);
      const errorMsg = err?.message || '网络异常，请稍后重试';
      const msg = state.messages.find((m) => m.id === assistantMsgId);
      if (msg) {
        msg.content = errorMsg;
        msg.state = 2;
      }
      state.loading = false;
    }
  };

  // 组件卸载时清理轮询
  React.useEffect(() => {
    return () => clearPolling();
  }, []);

  // 获取配额信息
  const fetchQuota = async () => {
    try {
      const res = await getCurrentQuota();
      if (res?.success && res?.data) {
        state.quota = res.data;
      }
    } catch (err) {
      console.error('获取配额信息失败:', err);
    }
  };

  // 组件加载时获取配额
  useEffect(() => {
    fetchQuota();
  }, []);

  // 打开图片预览
  const handlePreviewImage = (images, index = 0) => {
    // 兼容单个 URL 的调用方式
    const imageList = Array.isArray(images) ? images : [images];
    state.previewImage = { images: imageList, index };
  };

  // 打开视频预览
  const handlePreviewVideo = (videoUrl) => {
    state.previewVideo = videoUrl;
  };

  // 添加到对话（通知 InputArea 添加参考图）
  const handleAddToConversation = (imageUrl) => {
    if (inputAreaRef.current) {
      inputAreaRef.current.addReferenceImage(imageUrl);
    }
  };

  // 下载图片
  const handleDownloadImage = (imageUrl, msg) => {
    downloadFileUrl(imageUrl, getFileNameFromUrl(imageUrl));
  };

  // 消息操作菜单
  const getMessageMenu = (msg) => (
    <Dropdown.Menu>
      <Dropdown.Item>
        <div>复制</div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div>收藏</div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div style={{ color: '#ff4d4f' }}>删除</div>
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  // 渲染消息内容
  const renderMessageContent = (msg, index) => {
    if (msg.role === 'user') {
      return (
        <div className="userMessage">
          <div className="msgContent user">{msg.content}</div>
          {msg.images && msg.images.length > 0 && (
            <div className="msgImages">
              {msg.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt=""
                  className="msgImage"
                  onClick={() => handlePreviewImage(msg.images, idx)}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="assistantMessage">
        {msg.state === 0 && msg.mode === MODES.CHAT && (
          <div className="msgContent thinking">正在思考中...</div>
        )}
        {msg.state === 0 && msg.mode !== MODES.CHAT && (
          <GeneratingCard
            count={msg.quantity || 1}
            aspect={msg.genType === 2 ? '16/9' : '1/1'}
          />
        )}
        {msg.state === 1 && (
          <>
            {msg.mode === MODES.CHAT ? (
              <MarkdownRender content={msg.content} />
            ) : (
              <div className="msgContent">{msg.content}</div>
            )}
            {msg.images && msg.images.length > 0 && (
              <div className="msgImageGrid">
                {msg.images.map((img, idx) => (
                  <div
                    key={idx}
                    className="msgImageItem"
                    onClick={() => handlePreviewImage(msg.images, idx)}
                  >
                    <img src={img} alt="" />
                    <div className="msgImageActions">
                      <EnterOutlined
                        title="添加到对话"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToConversation(img);
                        }}
                      />
                      <DownloadOutlined
                        title="下载"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadImage(img, msg);
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {msg.videos && msg.videos.length > 0 && (
              <div className="msgVideoGrid">
                {msg.videos.map((video, idx) => (
                  <div
                    key={idx}
                    className="msgVideoItem"
                    onClick={() => handlePreviewVideo(video)}
                  >
                    <img
                      src={getVideoCoverUrl(video)}
                      alt=""
                      className="msgVideoCover"
                    />
                    <div className="msgVideoPlayIcon">
                      <VideoCameraOutlined
                        style={{ fontSize: 32, color: '#fff' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {msg.state === 2 && msg.mode !== MODES.CHAT && (
          <FailedCard
            count={msg.quantity || 1}
            aspect={msg.genType === 2 ? '16/9' : '1/1'}
          />
        )}
        {msg.state === 2 && msg.mode === MODES.CHAT && (
          <MarkdownRender content={msg.content} />
        )}
        {/* <Dropdown menu={getMessageMenu(msg)} trigger="click">
          <div className="msgActions">
            <CopyOutlined />
            <StarOutlined />
            <DeleteOutlined />
          </div>
        </Dropdown> */}
      </div>
    );
  };

  return (
    <div className="chatArea">
      {/* 顶部工具栏 */}
      <div className="chatHeader">
        <div className="headerLeft">
          {/* <SearchOutlined className="headerIcon" />
          <AppstoreOutlined
            className="headerIcon"
            onClick={onToggleAssetLibrary}
          /> */}
        </div>
        {/* 显示配额信息 */}
        {state.quota && (
          <div className="quotaInfo">
            额度使用情况：
            <Progress
              percentage={
                state.quota.totalQuota > 0
                  ? Math.min(
                      (state.quota.usedQuota / state.quota.totalQuota) * 100,
                      100,
                    )
                  : 0
              }
              strokeWidth={6}
              showText={false}
              style={{ width: 120, marginRight: 8 }}
            />
            <span className="quotaText">
              {formatQuota(state.quota.usedQuota)} /{' '}
              {formatQuota(state.quota.totalQuota)}
            </span>
          </div>
        )}
      </div>

      {/* 消息列表 */}
      <div className="messageList">
        {/* 切换会话 loading */}
        {conversationLoading && <MessageSkeleton />}

        {!conversationLoading &&
          state.messages.length === 0 &&
          !state.loading && (
            <div className="emptyState">
              <div className="emptyIcon">
                <PictureOutlined style={{ fontSize: 64, color: '#cbd5e1' }} />
              </div>
              <div className="emptyText">欢迎使用AI创作</div>
              <div className="emptySubText">选择下方模式，开始创作吧</div>
            </div>
          )}

        {!conversationLoading &&
          state.messages.map((msg, index) => (
            <div key={msg.id} className="messageWrapper">
              {renderMessageContent(msg, index)}
            </div>
          ))}

        <div ref={messagesEndRef} />
      </div>

      {/* 底部输入区 */}
      <InputArea
        ref={inputAreaRef}
        mode={mode}
        loading={state.loading}
        onSend={handleSend}
        onModeChange={onModeChange}
      />

      {/* 图片预览弹窗 */}
      {state.previewImage && (
        <ImagePreview
          images={state.previewImage.images}
          initialIndex={state.previewImage.index}
          onClose={() => (state.previewImage = null)}
          onAddToConversation={handleAddToConversation}
        />
      )}

      {/* 视频预览弹窗 */}
      {state.previewVideo && (
        <VideoPreview
          videoUrl={state.previewVideo}
          onClose={() => (state.previewVideo = null)}
        />
      )}
    </div>
  );
});

export default ChatArea;
